// src/lib/firebase.ts - Versione semplificata
import { initializeApp } from "firebase/app";
import { logger } from '@/lib/logger';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL, connectStorageEmulator } from "firebase/storage";
import { getFirestore, connectFirestoreEmulator, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, connectAuthEmulator, signInWithEmailAndPassword } from "firebase/auth";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { ADMIN_SERVICE_URL } from '@/config/endpoints';

// Usiamo i tipi di Vite preesistenti per import.meta.env

// Environment Guard: in development, fail fast if required vars are missing
if (import.meta.env.DEV) {
  const required = [
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_AUTH_DOMAIN'
  ];
  const missing = required.filter(k => !import.meta.env[k]);
  if (missing.length > 0) {
    throw new Error(
      `[ENV] Variabili d'ambiente mancanti in development: ${missing.join(', ')}. ` +
      `Copia frontend/.env.local.example in frontend/.env.local e riempi i valori.`
    );
  }
}

// Configurazione Firebase — nessun fallback silenzioso verso produzione
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "cdg-serverless.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef"
};

// Debug: stampa config attiva ed esponila su window.__APP_ENV__
logger.debug('🔧 firebaseConfig in uso:', {
  ...firebaseConfig,
  // non loggare informazioni sensibili extra oltre la API key
});
// @ts-ignore
window.__APP_ENV__ = firebaseConfig;

// Inizializza Firebase
const app = initializeApp(firebaseConfig);
// Usa europe-west8 come regione per le Cloud Functions
const functions = getFunctions(app, 'europe-west1');
const storage = getStorage(app);
const auth = getAuth(app);

const shouldUseEmulators = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';
const shouldUseAuthEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true';
const isLocalDevRealFirestore = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS !== 'true';

// Abilita offline persistence (multi-tab) solo in dev con Firestore reale (non con emulatori)
const db = isLocalDevRealFirestore
  ? initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    })
  : getFirestore(app);

// Promessa che si risolve quando l'utente è autenticato (anche anonimo)
let _resolveAuthReady: () => void;
export const authReady: Promise<void> = new Promise((resolve) => {
  _resolveAuthReady = resolve;
});

// Connetti agli emulatori in sviluppo. Auth usa ambiente reale per Google Sign-In.
if (shouldUseEmulators) {
  try {
    connectFunctionsEmulator(functions, "localhost", 5001);
    logger.debug("🔥 Functions emulator connesso (localhost:5001)");
  } catch (error) {
    logger.warn("⚠️ Impossibile connettersi all'emulatore Functions:", error);
  }

  try {
    const firestoreHost = (import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || 'localhost').replace('http://', '').replace('https://', '');
    const firestorePort = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 9090);
    connectFirestoreEmulator(db, firestoreHost, firestorePort);
    logger.debug(`🔥 Firestore emulator connesso (${firestoreHost}:${firestorePort})`);
  } catch (error) {
    logger.warn("⚠️ Impossibile connettersi all'emulatore Firestore:", error);
  }

  try {
    if (import.meta.env.VITE_USE_STORAGE_EMULATOR === 'true') {
      connectStorageEmulator(storage, "localhost", 9199);
      logger.debug("🔥 Storage emulator connesso (localhost:9199)");
    } else {
      logger.debug("ℹ️ Storage emulator DISABILITATO: uso bucket reale", firebaseConfig.storageBucket);
    }
  } catch (error) {
    logger.warn("⚠️ Impossibile connettersi all'emulatore Storage:", error);
  }
}

if (shouldUseAuthEmulator) {
  try {
    const authEmulatorUrl = import.meta.env.VITE_AUTH_EMULATOR_URL || 'http://localhost:9099';
    connectAuthEmulator(auth, authEmulatorUrl, { disableWarnings: true });
    logger.debug(`🔥 Auth emulator connesso (${authEmulatorUrl})`);
  } catch (error) {
    logger.warn('⚠️ Impossibile connettersi all\'emulatore Auth:', error);
  }
}

// Avviso configurazione: API key non valida o placeholder
if (!import.meta.env.VITE_FIREBASE_API_KEY || import.meta.env.VITE_FIREBASE_API_KEY === 'demo-api-key') {
  logger.warn('⚠️ Config Firebase non valida: VITE_FIREBASE_API_KEY è mancante o è un placeholder (demo-api-key). Aggiorna frontend/.env.');
}

// Token OAuth Google (access token, non Firebase ID token)
// Viene salvato dopo signInWithPopup per consentire chiamate alle Google API (es. Gmail)
let _googleAccessToken: string | null = null;
export const getGoogleAccessToken = (): string | null => _googleAccessToken;

// Helper per iniziare il login Google on-demand (usato anche come fallback automatico)
export const loginWithGoogle = async () => {
  if (shouldUseAuthEmulator) {
    const email = import.meta.env.VITE_FAKE_USER_EMAIL;
    const password = import.meta.env.VITE_FAKE_USER_PASSWORD;
    if (!email || !password) {
      throw new Error('VITE_FAKE_USER_EMAIL o VITE_FAKE_USER_PASSWORD non configurati per l\'emulatore Auth');
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      logger.debug('✅ Login completato tramite emulatore Auth con utente fittizio');
      return;
    } catch (err: any) {
      // Se l'utente non esiste, crealo automaticamente
      if (err?.code === 'auth/user-not-found') {
        logger.debug('🔧 Utente non trovato, creazione automatica...');
        const { createUserWithEmailAndPassword } = await import('firebase/auth');
        try {
          await createUserWithEmailAndPassword(auth, email, password);
          logger.debug('✅ Utente creato e login completato tramite emulatore Auth');
          return;
        } catch (createErr) {
          logger.error('❌ Errore creazione utente emulatore Auth:', createErr);
          throw createErr;
        }
      }
      logger.error('❌ Login emulatore Auth fallito:', err);
      throw err;
    }
  }

  const provider = new GoogleAuthProvider();
  
  // DOMAIN RESTRICTION: Limita login solo a @polo9.org (come Google Sites)
  provider.setCustomParameters({ 
    prompt: 'select_account',
    hd: 'polo9.org' // Hosted Domain - limita a questo dominio Google Workspace
  });

  // Scope per creare bozze Gmail con allegati
  provider.addScope('https://www.googleapis.com/auth/gmail.compose');
  
  try {
    const result = await signInWithPopup(auth, provider);
    console.log('[firebase.ts] signInWithPopup result:', {
      email: result.user.email,
      uid: result.user.uid,
      displayName: result.user.displayName,
    });
    
    // Verifica dominio dopo login (doppia sicurezza)
    const email = result.user.email;
    if (email && !email.endsWith('@polo9.org')) {
      await auth.signOut();
      throw new Error('Accesso consentito solo agli utenti @polo9.org');
    }

    // Salva access token Google per chiamate alle API Google (es. Gmail)
    const { GoogleAuthProvider: GAP } = await import('firebase/auth');
    const credential = GAP.credentialFromResult(result);
    if (credential?.accessToken) {
      _googleAccessToken = credential.accessToken;
    }
  } catch (popupErr) {
    logger.warn('Popup login fallito, provo con redirect...', popupErr);
    await signInWithRedirect(auth, provider);
  }
};

// Gestisci esiti di redirect (se usato come fallback)
(async () => {
  try {
    const result = await getRedirectResult(auth);
    
    // Verifica dominio anche per redirect
    if (result?.user?.email && !result.user.email.endsWith('@polo9.org')) {
      await auth.signOut();
      console.error('❌ Accesso negato: solo utenti @polo9.org possono accedere');
      // L'utente verrà reindirizzato alla pagina Unauthorized
    }
    // Salva access token da redirect
    if (result) {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        _googleAccessToken = credential.accessToken;
      }
    }
  } catch (e) {
    logger.warn('Gestione redirect login fallita:', e);
  }
})();

// Osserva lo stato auth: niente auto-login. Risolvi authReady dopo il primo evento.
onAuthStateChanged(auth, (user) => {
  if (user) {
    logger.debug('👤 Utente autenticato:', user.email || user.uid);
  } else {
    logger.warn('ℹ️ Utente non autenticato. Chiama loginWithGoogle() su azione utente per accedere.');
  }
  if (_resolveAuthReady) _resolveAuthReady();
});

// Helper per chiamare le Cloud Functions con validazione dei parametri migliorata
export const callFunction = async <T>(functionName: string, data?: any): Promise<T> => {
  try {
    logger.debug(`🔌 callFunction - Chiamata a ${functionName} con parametri:`, data);
    
    // Attendi che l'autenticazione sia pronta
    await authReady;
    
    // Verifica i parametri critici per operazioni specifiche
    if (functionName === 'deleteDataset' && (!data || !data.datasetId)) {
      console.error('❌ callFunction - deleteDataset chiamato senza datasetId valido:', data);
      throw new Error('datasetId è obbligatorio per eliminare un dataset');
    }
    
    // Assicuriamoci che data sia un oggetto e non null/undefined
    const safeData = data && typeof data === 'object' ? { ...data } : {};
    
    // Verifica e converti eventuali parametri undefined/null
    Object.keys(safeData).forEach(key => {
      if (safeData[key] === undefined) {
        logger.warn(`🔶 callFunction - Parametro ${key} è undefined, lo imposto a null`);
        safeData[key] = null;
      }
    });
    
    logger.debug(`🔌 callFunction - Parametri finali per ${functionName}:`, JSON.stringify(safeData));
    
    // Funzioni admin su Cloud Run (GDPR compliant - europe-west8)
    const adminFunctions = ['listUsers', 'getAuditLogs'];
    
    if (adminFunctions.includes(functionName)) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Autenticazione richiesta');
      }
      
      const token = await currentUser.getIdToken();
      const response = await fetch(`${ADMIN_SERVICE_URL}/${functionName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ data: safeData })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      logger.debug(`✅ callFunction - Risposta da ${functionName}:`, result);
      return result.result as T;
    }
    
    // Usa httpsCallable per le altre funzioni
    const functionRef = httpsCallable<any, T>(functions, functionName);
    const result = await functionRef(safeData);
    logger.debug(`✅ callFunction - Risposta da ${functionName}:`, result.data);
    return result.data;
    
  } catch (error) {
    console.error(`❌ Errore nella chiamata alla funzione ${functionName}:`, error);
    throw error;
  }
};

// Helper per caricare file su Storage
export const uploadFile = async (
  file: File,
  path: string,
  metadata?: { contentType?: string; customMetadata?: Record<string, string> }
): Promise<string> => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file, metadata);
    return await getDownloadURL(snapshot.ref);
  } catch (error) {
    console.error('Errore nel caricamento del file:', error);
    throw error;
  }
};

export { app, functions, storage, auth, db };

// Helper di logout
export async function logout(): Promise<void> {
  await signOut(auth);
}

// ============================================
// FIREBASE CLOUD MESSAGING (FCM)
// ============================================

// VAPID Key per FCM (da Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

// URL admin-service
let messaging: ReturnType<typeof getMessaging> | null = null;

/**
 * Inizializza Firebase Cloud Messaging
 * Restituisce null se non supportato (es. Safari senza service worker)
 */
export async function initializeMessaging() {
  try {
    const supported = await isSupported();
    if (!supported) {
      logger.warn('⚠️ FCM non supportato in questo browser');
      return null;
    }
    
    messaging = getMessaging(app);
    logger.debug('✅ FCM inizializzato');
    return messaging;
  } catch (error) {
    logger.error('❌ Errore inizializzazione FCM:', error);
    return null;
  }
}

/**
 * Richiede permesso notifiche e ottiene token FCM
 * Salva il token nel backend per invio notifiche push
 */
export async function requestNotificationPermission(): Promise<string | null> {
  try {
    // Verifica supporto
    if (!('Notification' in window)) {
      logger.warn('⚠️ Notifiche non supportate');
      return null;
    }
    
    // Richiedi permesso
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      logger.warn('⚠️ Permesso notifiche negato');
      return null;
    }
    
    // Inizializza messaging se necessario
    if (!messaging) {
      await initializeMessaging();
    }
    
    if (!messaging) {
      return null;
    }
    
    // Ottieni token
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    
    if (token) {
      logger.debug('✅ Token FCM ottenuto');
      // Salva token nel backend
      await saveFcmToken(token);
      return token;
    }
    
    return null;
  } catch (error) {
    logger.error('❌ Errore richiesta permesso notifiche:', error);
    return null;
  }
}

/**
 * Salva token FCM nel backend per l'utente corrente
 */
async function saveFcmToken(token: string): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) return;
    
    const authToken = await user.getIdToken();
    await fetch(`${ADMIN_SERVICE_URL}/notifications/fcm-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ token })
    });
    
    logger.debug('✅ Token FCM salvato nel backend');
  } catch (error) {
    logger.error('❌ Errore salvataggio token FCM:', error);
  }
}

/**
 * Ascolta messaggi FCM in foreground
 * Mostra notifica toast quando l'app è aperta
 */
export function onForegroundMessage(callback: (payload: unknown) => void): () => void {
  if (!messaging) {
    return () => {};
  }
  
  return onMessage(messaging, (payload) => {
    logger.debug('📬 Messaggio FCM ricevuto in foreground:', payload);
    callback(payload);
  });
}